/**
 * Comprobación de valores repetidos en los maestros.
 *
 * Los siete maestros tenían la misma comprobación escrita a mano, y las siete
 * fallaban de las mismas tres formas:
 *
 * 1. **Comparaban con un igual exacto sobre el índice de Dexie**, que distingue
 *    mayúsculas y espacios. "Matutino", "matutino" y "Matutino " eran tres
 *    valores distintos y los tres entraban. De ahí salen los repetidos que se
 *    ven en las tablas.
 * 2. **Sólo el alta comprobaba.** Se podía crear "Matutino" y después renombrar
 *    otro turno a "Matutino" sin que nada lo impidiera: la única puerta con
 *    cerradura era el alta y la de al lado estaba abierta.
 * 3. **No excluían los eliminados.** Como el borrado marca el registro y lo
 *    conserva hasta que Business Central lo confirme, en esa ventana borrar algo
 *    e intentar crearlo otra vez decía "ya existe" señalando un registro que no
 *    se ve en ningún listado.
 *
 * Se resuelve una vez acá en vez de siete veces mal.
 */

/**
 * Forma con la que se comparan dos valores para decidir si son el mismo.
 *
 * Colapsa también los espacios interiores. Es lo correcto para nombres y
 * descripciones --"JUAN  PEREZ" es tecleo, no otro cliente-- pero **no** para
 * los códigos de tienda, donde el espacio es parte del código ("MALL DEL SOL").
 * Por eso `normalizarCodigoTienda` sigue viviendo aparte.
 */
export const normalizarTexto = (valor) =>
  String(valor ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");

/**
 * Busca un registro vivo que ya use este valor.
 *
 * Recorre la tabla en vez de usar el índice: el índice sólo sabe comparar
 * exacto, que es justamente lo que dejaba pasar los repetidos. Son maestros de
 * pocas filas y esto corre al guardar, no al teclear.
 *
 * @param {object} tabla - Tabla de Dexie.
 * @param {string} campo - Campo a comprobar.
 * @param {*} valor - Valor entrante.
 * @param {{excluyendoId?: number|string}} [opciones] - El propio registro, al editar.
 * @returns {Promise<Object.<string, any>|null>} El registro que lo ocupa, o null.
 */
export const buscarValorEnUso = async (tabla, campo, valor, opciones = {}) => {
  const buscado = normalizarTexto(valor);
  if (!buscado) return null;

  const { excluyendoId = null } = opciones;
  const todos = await tabla.toArray();

  return (
    todos.find(
      (registro) =>
        !registro.deleted &&
        registro.id !== excluyendoId &&
        normalizarTexto(registro[campo]) === buscado
    ) || null
  );
};

/**
 * Si el valor entrante cambia respecto del guardado.
 *
 * Al editar sólo se comprueba lo que cambió. Si se comprobara siempre, los
 * registros que **ya** están repetidos quedarían imposibles de guardar:
 * corregirle un dato a uno chocaría con su gemelo y no habría forma de salir.
 * Y como además se excluye el propio registro, guardar sin tocar el campo único
 * nunca se bloquea.
 */
export const campoCambio = (datos, actual, campo) =>
  datos[campo] !== undefined && normalizarTexto(datos[campo]) !== normalizarTexto(actual[campo]);

/**
 * Comprueba un campo al editar y devuelve el registro que lo ocupa, si lo hay.
 * Junta las dos reglas de arriba, que es como se usan siempre.
 *
 * @returns {Promise<Object.<string, any>|null>}
 */
export const buscarValorEnUsoAlEditar = async (tabla, campo, datos, actual) => {
  if (!campoCambio(datos, actual, campo)) return null;

  return buscarValorEnUso(tabla, campo, datos[campo], { excluyendoId: actual.id });
};
